import React from 'react';
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Space, Typography, Upload } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createVideo } from '../../services/video';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';
import { getAllLessons } from '../../services/lesson';
import { getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
// Removed rich text editor; using textarea for description

const itemName = 'Video';
const { Text, Title } = Typography;

const CreateVideo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  // Fetch lessons and curriculums to display combined label
  const { data: lessonListRes } = useQuery({
    queryKey: ['allLessonsForVideoForm'],
    queryFn: () => getAllLessons(),
    staleTime: 1000 * 60 * 10,
  });
  const { data: curriculumListRes } = useQuery({
    queryKey: ['allCurriculums'],
    queryFn: () => getCurriculums({ page: 1, limit: 10000, status: [1] }),
    staleTime: 1000 * 60 * 10,
  });
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategoriesForVideoForm'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const lessonOptions = React.useMemo(() => {
    const lessons = Array.isArray(lessonListRes?.data) ? lessonListRes.data : [];
    const currs = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const curriculumTitle = currs.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const curriculumCat = currs.reduce((acc, c) => { acc[c.id] = c.category_id || 0; return acc; }, {});
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toCatPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id;
      const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) {
        guard.add(cur);
        parts.unshift(titleMap[cur] || '');
        cur = parentMap[cur] || 0;
      }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const opts = lessons.map((l) => {
      const cid = curriculumCat[l.curriculum_id] || 0;
      const catPath = toCatPath(cid);
      const curTitle = curriculumTitle[l.curriculum_id] || '';
      const lessonTitle = l.title || '';
      const label = `${catPath || '—'} - ${curTitle} - ${lessonTitle}`.trim();
      return { value: l.id, label, _cat: (catPath || '').toLowerCase(), _cur: (curTitle || '').toLowerCase(), _lesson: (lessonTitle || '').toLowerCase() };
    });
    opts.sort((a, b) => a._cat.localeCompare(b._cat) || a._cur.localeCompare(b._cur) || a._lesson.localeCompare(b._lesson));
    return opts;
  }, [lessonListRes, curriculumListRes, allCategoriesRes]);

  const { mutate: mutateCreate, isPending } = useMutation({
    mutationFn: createVideo,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Thêm mới ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (data) => {
    const payload = {
      lesson_id: Number(data?.lesson_id || 0),
      title: data?.title,
      thumb: data?.thumb,
      video_link: data?.video_link,
      description: data?.description,
      duration_seconds: Number(data?.duration_seconds || 0),
      order: Number(data?.order || 1),
      status: data?.status,
    };
    mutateCreate(payload);
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) return false;
    const url = await uploadImageCDN(file, () => {}, 'toancohien_video');
    if (url) {
      notification.success({ message: 'Tải lên thành công!' });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

   // Prefill lesson_id if provided via query param
  React.useEffect(() => {
    const lessonStr = searchParams.get('lesson_id');
    const lessonId = lessonStr ? Number(lessonStr) : 0;
    if (lessonId > 0) {
      form.setFieldsValue({ lesson_id: lessonId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form name="create-video" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}
          >
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="lesson_id"
            label={<Text>Bài học <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn bài học' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Chọn bài học"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={lessonOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="video_link" label={<Text>Link video</Text>}>
            <Input placeholder={`Nhập link video (chuỗi văn bản)`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="duration_seconds" label={<Text>Thời lượng (giây)</Text>} initialValue={0}>
            <InputNumber min={0} className="w-full" placeholder="Nhập thời lượng" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="order" label={<Text>Thứ tự</Text>} initialValue={1}>
            <InputNumber min={1} step={1} precision={0} className="w-full" placeholder="Nhập thứ tự (số nguyên dương)" />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="description" label={<Text>Mô tả</Text>}>
            <Input.TextArea rows={6} placeholder="Nhập mô tả..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={<Text>Trạng thái <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            initialValue={1}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateVideo;
